import React, { useEffect, useState, useMemo } from "react";
import styled from "@emotion/styled";

interface PreviewDeployment {
  branch: string;
  previewId: string;
  commitSha: string;
  author: string;
  deployedAt: string;
  url: string;
}

interface PreviewDeploymentVersionProps {
  /** Position on screen - defaults to right */
  position?: "left" | "right";
  /** Show banner on develop builds (not just preview deployments) */
  showOnDevelop?: boolean;
  /** URL to navigate to for develop builds (used when on preview to switch to develop) */
  developUrl?: string;
}

type DeploymentContext = "develop" | "preview" | "none";

const VersionContainer = styled.div<{ position: "left" | "right" }>`
  position: fixed;
  ${(props) => (props.position === "left" ? "left: 0;" : "right: 0;")}
  top: 50%;
  transform: translateY(-50%)
    ${(props) =>
      props.position === "left" ? "rotate(-90deg)" : "rotate(90deg)"};
  transform-origin: ${(props) =>
    props.position === "left" ? "left bottom" : "right top"};
  z-index: 9999;
  pointer-events: auto;
`;

const VersionLabel = styled.select<{ isLatest: boolean }>`
  background: transparent;
  border: none;
  color: ${(props) =>
    props.isLatest ? "rgba(0, 0, 0, 0.3)" : "rgba(255, 0, 0, 0.5)"};
  font-size: 10px;
  font-family: monospace;
  cursor: pointer;
  padding: 4px 8px;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  &:hover {
    color: ${(props) =>
      props.isLatest ? "rgba(0, 0, 0, 0.6)" : "rgba(255, 0, 0, 0.8)"};
  }

  &:focus {
    color: ${(props) => (props.isLatest ? "rgba(0, 0, 0, 0.8)" : "red")};
  }

  option,
  optgroup {
    color: black;
    background: white;
  }
`;

function parseManifest(content: string): PreviewDeployment[] {
  const lines = content.split("\n");
  const deployments: PreviewDeployment[] = [];

  for (const line of lines) {
    if (!line.trim() || line.startsWith("#")) continue;

    const parts = line.split("|");
    if (parts.length >= 6) {
      deployments.push({
        branch: parts[0],
        previewId: parts[1],
        commitSha: parts[2],
        author: parts[3],
        deployedAt: parts[4],
        url: parts[5]
      });
    }
  }

  return deployments;
}

function extractPreviewIdFromUrl(): string | null {
  const path = window.location.pathname;
  const match = path.match(/\/preview-([a-f0-9]{8})\/?/);
  return match ? match[1] : null;
}

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr.replace(" UTC", "Z"));
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return dateStr;
  }
}

function getDeploymentContext(
  previewId: string | null,
  showOnDevelop: boolean
): DeploymentContext {
  if (previewId) return "preview";
  if (showOnDevelop) return "develop";
  return "none";
}

function groupDeploymentsByBranch(
  deployments: PreviewDeployment[]
): Map<string, PreviewDeployment[]> {
  const groups = new Map<string, PreviewDeployment[]>();

  for (const deployment of deployments) {
    const existing = groups.get(deployment.branch) || [];
    existing.push(deployment);
    groups.set(deployment.branch, existing);
  }

  // Sort each group by date descending
  for (const [, deps] of groups) {
    deps.sort(
      (a, b) =>
        new Date(b.deployedAt).getTime() - new Date(a.deployedAt).getTime()
    );
  }

  return groups;
}

function PreviewDeploymentVersion({
  position = "right",
  showOnDevelop = false,
  developUrl
}: PreviewDeploymentVersionProps) {
  const [deployments, setDeployments] = useState<PreviewDeployment[]>([]);
  const [currentPreviewId, setCurrentPreviewId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const context = useMemo(() => {
    return getDeploymentContext(currentPreviewId, showOnDevelop);
  }, [currentPreviewId, showOnDevelop]);

  useEffect(() => {
    const previewId = extractPreviewIdFromUrl();
    setCurrentPreviewId(previewId);

    const deploymentContext = getDeploymentContext(previewId, showOnDevelop);

    if (deploymentContext === "none") {
      setLoading(false);
      return;
    }

    const manifestUrl = `${window.location.origin}/preview-manifest.txt`;

    fetch(manifestUrl, { cache: "no-store" })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((content) => {
        const parsed = parseManifest(content);
        setDeployments(parsed);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("An unknown error occurred");
        }
        setLoading(false);
      });
  }, [showOnDevelop]);

  // Get develop deployment info from manifest
  const developDeployment = useMemo(() => {
    return deployments.find((d) => d.branch === "develop") ?? null;
  }, [deployments]);

  // Get current deployment (for preview context)
  const currentDeployment = useMemo(() => {
    if (!currentPreviewId) return null;
    return deployments.find((d) => d.previewId === currentPreviewId) ?? null;
  }, [deployments, currentPreviewId]);

  // Get deployments for current branch (for preview context)
  const branchDeployments = useMemo(() => {
    if (!currentDeployment) return [];
    return deployments
      .filter((d) => d.branch === currentDeployment.branch)
      .sort(
        (a, b) =>
          new Date(b.deployedAt).getTime() - new Date(a.deployedAt).getTime()
      );
  }, [deployments, currentDeployment]);

  // Group all non-develop deployments by branch (for develop context)
  const groupedDeployments = useMemo(() => {
    const nonDevelopDeployments = deployments.filter(
      (d) => d.branch !== "develop"
    );
    return groupDeploymentsByBranch(nonDevelopDeployments);
  }, [deployments]);

  // Sort branch names alphabetically
  const sortedBranches = useMemo(() => {
    return Array.from(groupedDeployments.keys()).sort();
  }, [groupedDeployments]);

  const isLatest = useMemo(() => {
    if (context === "develop") return true;
    if (!currentDeployment || branchDeployments.length === 0) return true;
    return branchDeployments[0].previewId === currentPreviewId;
  }, [context, branchDeployments, currentPreviewId, currentDeployment]);

  if (context === "none") return null;
  if (loading) return null;
  if (error) return null;

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedUrl = e.target.value;
    if (selectedUrl && selectedUrl !== window.location.href) {
      window.location.href = selectedUrl;
    }
  };

  const truncateBranch = (branch: string, maxLength = 18) =>
    branch.length > maxLength ? `${branch.slice(0, maxLength)}…` : branch;

  // Render for DEVELOP context
  if (context === "develop") {
    const displayText = developDeployment
      ? `develop @ ${formatDate(developDeployment.deployedAt)}`
      : "develop";

    const currentUrl = window.location.href;

    return (
      <VersionContainer position={position}>
        <VersionLabel
          isLatest={true}
          value={currentUrl}
          onChange={handleChange}
          title="Switch to a preview deployment"
        >
          <option value={currentUrl}>{displayText}</option>
          {sortedBranches.map((branch) => {
            const branchDeps = groupedDeployments.get(branch) || [];
            return (
              <optgroup key={branch} label={truncateBranch(branch, 30)}>
                {branchDeps.map((d) => (
                  <option key={d.previewId} value={d.url}>
                    {d.commitSha.slice(0, 8)} ({formatDate(d.deployedAt)})
                  </option>
                ))}
              </optgroup>
            );
          })}
        </VersionLabel>
      </VersionContainer>
    );
  }

  // Render for PREVIEW context
  const developOptionUrl = developUrl || developDeployment?.url;

  // Fallback display for unknown preview
  const fallbackDisplay = `preview-${currentPreviewId}`;

  return (
    <VersionContainer position={position}>
      <VersionLabel
        isLatest={isLatest}
        value={currentDeployment?.url || ""}
        onChange={handleChange}
        title={
          isLatest
            ? "Latest preview for this branch"
            : "Newer preview available for this branch"
        }
      >
        {branchDeployments.length > 0 ? (
          <optgroup
            label={`${truncateBranch(currentDeployment?.branch || "", 25)}${!isLatest ? " - OUTDATED" : ""}`}
          >
            {branchDeployments.map((d) => (
              <option key={d.previewId} value={d.url}>
                {d.commitSha.slice(0, 8)} ({formatDate(d.deployedAt)})
                {d.previewId === currentPreviewId ? " ←" : ""}
              </option>
            ))}
          </optgroup>
        ) : (
          <option value="">{fallbackDisplay}</option>
        )}
        {developOptionUrl && (
          <optgroup label="Switch to">
            <option value={developOptionUrl}>
              develop
              {developDeployment
                ? ` (${formatDate(developDeployment.deployedAt)})`
                : ""}
            </option>
          </optgroup>
        )}
      </VersionLabel>
    </VersionContainer>
  );
}

export default PreviewDeploymentVersion;
